import { ref, Ref } from 'vue'

export enum BucketDefaultIDs {
  USERS = 'usersBucket',
  ARMADORES = 'armadoresBucket',
  EMBARCACIONES = 'embarcacionesBucket',
  PROVEEDORES = 'proveedoresBucket',
  TRIPULANTES = 'tripulantesBucket',
  DOCUMENTOS = 'documentosBucket',
  SOLICITUDES = 'solicitudesBucket',
  CAMBIOS = 'cambiosBucket',
  VIAJES = 'viajesBucket',
  SERVICES = 'servicesBucket',
  VEHICLE_SERVICES = 'vehicleServicesBucket',
  BITACORA = 'bitacoraBucket',
  TARIFAS = 'tarifasBucket',
  COBROS = 'cobrosBucket',
  NOTIFICATIONS = 'notificationsBucket',
  PERMISOS = 'permisosBucket',
  ROLES_ALTERNATIVOS = 'rolesAlternativosBucket',
  HORARIOS = 'horariosBucket',
  CONFIGURACIONES_CREDENCIAL = 'configuracionesCredencialBucket',
  CONFIG_GLOBAL_CREDENCIAL = 'configGlobalCredencialBucket',
  ESPORADICOS = 'esporadicosBucket',
  OTROS = 'otherBucket',
}

export interface DMS_Operation {
  operation: Function
  reloadAfter: boolean
}

interface DSM_Bucket<T> {
  lastData: Array<T>
  data: Ref<Array<T>>
  lastQueryTime: number
  refreshRatio: number // in milliseconds
  dataGetter: () => Promise<Array<T>>
  preload: boolean
  subscribers: Set<(resp: Array<T>) => void>
  operations: Map<string, DMS_Operation>
  getDataPromise: Promise<any> | null
}

/*
  DataStorageManager
  Encargado de gestionar el almacenamiento en memoria de los datos obtenidos de la API. Debe optimizar las consultas para no saturar la API y mantener los datos frescos.
  Cada "bucket" de datos tiene un ID único, un array de datos, la hora de la última consulta y un ratio de refresco (en ms).
    El ratio de refresco indica cada cuánto tiempo se debe volver a consultar la API para mantener los datos actualizados.
    En un futuro el ratio de refresco podría ser dinámico, dependiendo del tipo de datos o la frecuencia de cambios esperada.
  Cuando los datos de un bucket son solicitados, se verifica si ha pasado el tiempo del ratio de refresco desde la última consulta.
    Si ha pasado el tiempo, se realiza una nueva consulta a la API y se actualizan los datos y la hora de la última consulta.
    Si no ha pasado el tiempo, se devuelven los datos almacenados en memoria.
  Cuando se actualizan datos a través de la API se deben actualizar en las paginas que los solicitaron.
  Las consultas se deben hacer en paralelo para optimizar el tiempo de espera.
*/
class DataStorageManager {
  private buckets: Map<BucketDefaultIDs, DSM_Bucket<any>>

  constructor() {
    this.buckets = new Map<BucketDefaultIDs, DSM_Bucket<any>>()
  }

  public registerBucket<T>(
    getter: () => Promise<Array<T>>,
    configuration: {
      refreshRatio: number
      preload?: boolean
      bucketID?: BucketDefaultIDs
      operations?: Map<string, DMS_Operation> // Nueva propiedad para operaciones
    },
  ): string {
    const bucketID = configuration.bucketID || this.generateBucketID()
    const bucket: DSM_Bucket<T> = {
      lastData: [],
      data: ref([]),
      lastQueryTime: 0,
      refreshRatio: configuration.refreshRatio,
      dataGetter: getter,
      preload: configuration.preload || false,
      subscribers: new Set<(resp: Array<T>) => void>(),
      operations: configuration.operations || new Map<string, DMS_Operation>(),
      getDataPromise: null,
    }
    this.buckets.set(bucketID, bucket)
    if (configuration.preload) {
      this.refreshBucket(bucketID)
    }
    if (configuration.refreshRatio > 0) {
      setInterval(() => {
        this.refreshBucket(bucketID)
      }, configuration.refreshRatio)
    }
    return bucketID
  }

  public subscribeBucket<T>(bucketID: BucketDefaultIDs, callback: (resp: Array<T>) => void): Ref<Array<T>> {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) throw new Error(`Bucket with ID ${bucketID} not found`)
    bucket.subscribers.add(callback)
    if (bucket.lastQueryTime > 0) callback(bucket.data.value)

    this.refreshBucket(bucketID)

    return bucket.data
  }

  public async getBucketData<T>(bucketID: BucketDefaultIDs): Promise<Array<T>> {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) throw new Error(`Bucket with ID ${bucketID} not found`)
    await this.refreshBucket(bucketID)
    return bucket.data.value
  }

  public getCurrentBucketData<T>(bucketID: BucketDefaultIDs): Array<T> {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) throw new Error(`Bucket with ID ${bucketID} not found`)
    return bucket.data.value
  }

  public async refreshBucket(bucketID: BucketDefaultIDs) {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) return
    try {
      if (bucket.lastQueryTime === 0 || Date.now() - bucket.lastQueryTime >= bucket.refreshRatio) {
        if (!bucket.getDataPromise) bucket.getDataPromise = bucket.dataGetter()
        const newData = await bucket.getDataPromise
        bucket.data.value = newData
        bucket.lastQueryTime = Date.now()
      }
      const bucketData = bucket.data.value
      if (bucket.lastData !== bucketData) bucket.subscribers.forEach((callback) => callback(bucketData))
      bucket.lastData = bucketData
      bucket.getDataPromise = null // Clear promise after resolved
    } catch (error) {
      console.error(`Error refreshing bucket ${bucketID}:`, error)
    }
  }

  public async forceRefreshBucket(bucketID: BucketDefaultIDs): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const bucket = this.buckets.get(bucketID)
      if (!bucket) {
        reject(`Bucket with ID ${bucketID} not found`)
        return
      }
      try {
        // Check if exist promise and it not resolved yet
        if (!bucket.getDataPromise) {
          bucket.getDataPromise = bucket.dataGetter()
        }
        let jumpOut = false
        setTimeout(() => {
          if (!jumpOut) {
            console.warn(`Data promise for bucket ${bucketID} is taking too long, forcing exit`)
            reject(`Timeout while refreshing bucket ${bucketID}`)
          }
        }, 10000) // 10 seconds timeout
        const newData = await bucket.getDataPromise
        jumpOut = true
        bucket.data.value = newData
        bucket.lastQueryTime = Date.now()
        const bucketData = bucket.data.value
        if (bucket.lastData !== bucketData) bucket.subscribers.forEach((callback) => callback(bucketData))
        bucket.lastData = bucketData
        bucket.getDataPromise = null // Clear promise after resolved
      } catch (error) {
        console.error(`Error force refreshing bucket ${bucketID}:`, error)
        reject(error)
      }
      resolve()
    })
  }

  public searchDataInBucket<T>(bucketID: BucketDefaultIDs, predicate: (item: T) => boolean): T | undefined {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) return undefined
    return bucket.data.value.find(predicate)
  }

  public async getAndSearchDataInBucket<T>(
    bucketID: BucketDefaultIDs,
    predicate: (item: T) => boolean,
  ): Promise<T | undefined> {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) return undefined
    if (bucket.lastQueryTime === 0 || Date.now() - bucket.lastQueryTime >= bucket.refreshRatio) {
      await this.refreshBucket(bucketID)
    } else if (bucket.getDataPromise) {
      await bucket.getDataPromise
    }
    return bucket.data.value.find(predicate)
  }

  public filterDataInBucket<T>(bucketID: BucketDefaultIDs, predicate: (item: T) => boolean): Array<T> {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) return []
    return bucket.data.value.filter(predicate)
  }

  public async getAndFilterDataInBucket<T>(
    bucketID: BucketDefaultIDs,
    predicate: (item: T) => boolean,
  ): Promise<Array<T>> {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) return []
    if (bucket.lastQueryTime === 0 || Date.now() - bucket.lastQueryTime >= bucket.refreshRatio) {
      await this.refreshBucket(bucketID)
    } else if (bucket.getDataPromise) {
      await bucket.getDataPromise
    }
    return bucket.data.value.filter(predicate)
  }

  public async performOperation(bucketID: BucketDefaultIDs, operationName: string, ...args: any[]): Promise<any> {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) throw new Error(`Bucket with ID ${bucketID} not found`)
    const selectedOperation = bucket.operations.get(operationName)
    if (selectedOperation) {
      const result = await selectedOperation.operation(...args)
      if (selectedOperation.reloadAfter) await this.forceRefreshBucket(bucketID)
      return result
    } else {
      throw new Error(`Operation ${operationName} not found in bucket ${bucketID}`)
    }
  }

  /**
   * Igual que performOperation pero NUNCA refresca el bucket, ignorando el flag
   * reloadAfter de la operación. Pensado para encadenar varias mutaciones sobre el
   * mismo bucket (p. ej. insertar varias solicitudes en una sola acción) sin pagar
   * una recarga completa de la tabla tras cada una; el llamador hace un único
   * forceRefreshBucket(...) al final. Evita N descargas redundantes en serie.
   */
  public async performOperationNoReload(
    bucketID: BucketDefaultIDs,
    operationName: string,
    ...args: any[]
  ): Promise<any> {
    const bucket = this.buckets.get(bucketID)
    if (!bucket) throw new Error(`Bucket with ID ${bucketID} not found`)
    const selectedOperation = bucket.operations.get(operationName)
    if (!selectedOperation) throw new Error(`Operation ${operationName} not found in bucket ${bucketID}`)
    return await selectedOperation.operation(...args)
  }

  private generateBucketID() {
    //return `bucket_${Date.now()}`;
    return BucketDefaultIDs.OTROS
  }

  // Check if bucket exists
  public bucketExists(bucketID: BucketDefaultIDs): boolean {
    return this.buckets.has(bucketID)
  }
}

const dataStorageManager = new DataStorageManager()
export default dataStorageManager
